/**
 * Evidence Extractor Page
 *
 * Main page for the Evidence Extractor application.
 * Updated for unified routing with React Router navigation and centralized authentication.
 * Currently a placeholder for future functionality.
 *
 * Requirements: 1.5, 2.5, 3.1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentSearchRegular, ArrowUploadRegular, CheckmarkCircleRegular, SignOutRegular } from '@fluentui/react-icons';
import { useAuth } from '../../../shared/src/auth';
import CAALogo from '../assets/caa-logo.svg';
import styles from './EvidenceExtractor.module.css';

export const EvidenceExtractor: React.FC = () => {
  const { user, isAuthenticated, isLoading, hasRole, logout } = useAuth();

  // Check if user has access to other apps besides Evidence Extractor
  const hasAccessToOtherApps = hasRole('admin') || hasRole('policy_navigator');

  // Show loading state while authentication is being determined
  if (isLoading) {
    return (
      <div className={styles.container}>
        <header className={styles.header} role="banner">
          <div className={styles.headerContainer}>
            <div className={styles.headerTitleContainer}>
              <img src={CAALogo} className={styles.headerLogo} alt="UK Civil Aviation Authority" />
              <div className={styles.headerTitleGroup}>
                <h3 className={styles.headerTitle}>SORA Evidence Extractor</h3>
                <span className={styles.headerSubtitle}>Document Analysis & Compliance</span>
              </div>
            </div>
          </div>
        </header>
        <main className={styles.main}>
          <div className={styles.content}>
            <div className={styles.loading}>
              <p>Loading...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header} role="banner">
        <div className={styles.headerContainer}>
          <div className={styles.headerTitleContainer}>
            <img src={CAALogo} className={styles.headerLogo} alt="UK Civil Aviation Authority" />
            <div className={styles.headerTitleGroup}>
              <h3 className={styles.headerTitle}>SORA Evidence Extractor</h3>
              <span className={styles.headerSubtitle}>Document Analysis & Compliance</span>
            </div>
          </div>
          <div className={styles.headerRightButtons}>
            {/* User name/email display */}
            {isAuthenticated && user && (user.name || user.email) && (
              <div className={styles.userInfo}>
                <span className={styles.userEmail}>{user.name || user.email}</span>
              </div>
            )}
            {hasAccessToOtherApps ? (
              <Link
                to="/"
                className={styles.exitButtonContainer}
                role="button"
                tabIndex={0}
                aria-label="Exit to Dashboard"
                title="Return to Dashboard"
              >
                <SignOutRegular className={styles.exitButton} />
              </Link>
            ) : (
              <button
                className={styles.logoutButton}
                onClick={logout}
                aria-label="Logout"
                title="Logout"
              >
                <SignOutRegular className={styles.logoutIcon} />
                <span>Logout</span>
              </button>
            )}
          </div>
        </div>
      </header>
      <main className={styles.main}>
        <div className={styles.content}>
          <div className={styles.hero}>
            <DocumentSearchRegular className={styles.heroIcon} />
            <h1 className={styles.title}>SORA Evidence Extractor</h1>
            <p className={styles.subtitle}>
              Extract and analyse evidence from your operational documentation
            </p>
            {isAuthenticated && user && (
              <p className={styles.welcomeMessage}>
                Welcome, {user.name || user.email}
              </p>
            )}
          </div>

          <div className={styles.placeholder}>
            <h2 className={styles.comingSoon}>Coming Soon</h2>
            <p className={styles.description}>
              The Evidence Extractor will help you identify and extract key evidence
              from UK drone regulations and CAA publications.
            </p>

            <div className={styles.features}>
              <div className={styles.feature}>
                <ArrowUploadRegular className={styles.featureIcon} />
                <h3>Document Upload</h3>
                <p>Upload regulatory documents for analysis</p>
              </div>
              <div className={styles.feature}>
                <DocumentSearchRegular className={styles.featureIcon} />
                <h3>Evidence Extraction</h3>
                <p>AI-powered extraction of key regulatory evidence</p>
              </div>
              <div className={styles.feature}>
                <CheckmarkCircleRegular className={styles.featureIcon} />
                <h3>Compliance Mapping</h3>
                <p>Map evidence to compliance requirements</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
